import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuditLogs from './AuditLogs';
import { validateData, AdminLoginSchema, TenantSchema, AdminUserSchema, PasswordChangeSchema } from '../utils/validationSchemas';
import { useSessionTimeout } from '../hooks/useSessionTimeout';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [tenants, setTenants] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [users, setUsers] = useState({ adminUsers: [] });
  const [tenantUsers, setTenantUsers] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalUsers: 0 });
  const [showAddTenant, setShowAddTenant] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showUsers, setShowUsers] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'tenant-users', or 'audit-logs'
  const [selectedTenant, setSelectedTenant] = useState(null);
  // Use ref for sensitive clientSecret (not stored in state)
  const clientSecretRef = useRef('');

  const [newTenant, setNewTenant] = useState({
    name: '',
    domain: '',
    clientId: '',
    // clientSecret removed from state for security
    tenantId: '',
    authority: '',
    allowPasswordReset: true,
    allowAccountUnlock: true
  });
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    email: '',
    password: '',
    role: 'admin'
  });
  const [passwordChange, setPasswordChange] = useState({
    currentPassword: '',
    newPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [adminData, setAdminData] = useState(null);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // Session timeout: auto-logout after 15 minutes of inactivity
  useSessionTimeout(15, () => {
    if (isAuthenticated) {
      handleLogout();
    }
  });

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchTenants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate input before sending
    const validation = validateData(AdminLoginSchema, loginData);
    if (!validation.isValid) {
      setError(validation.error);
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/auth/admin/login', validation.data);
      const newToken = response.data.token;
      setToken(newToken);
      setAdminData(response.data.admin);
      setIsAuthenticated(true);
      await fetchTenants(newToken);
    } catch (error) {
      // Use generic error message for security
      setError('Invalid credentials or login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTenants = async (authToken = token) => {
    try {
      const response = await axios.get('/api/admin/tenants', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setTenants(response.data);
      setError(''); // Clear any previous errors
    } catch (error) {
      setError('Failed to fetch tenants');
    }
  };

  const handleAddTenant = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate input before sending
    const validation = validateData(TenantSchema, newTenant);
    if (!validation.isValid) {
      setError(validation.error);
      setIsLoading(false);
      return;
    }

    try {
      // Include clientSecret from ref (not from state)
      const tenantData = {
        ...validation.data,
        clientSecret: clientSecretRef.current
      };

      await axios.post('/api/admin/tenants', tenantData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Clear sensitive data
      clientSecretRef.current = '';

      setNewTenant({
        name: '',
        domain: '',
        clientId: '',
        tenantId: '',
        authority: '',
        allowPasswordReset: true,
        allowAccountUnlock: true
      });
      setShowAddTenant(false);
      await fetchTenants();
    } catch (error) {
      setError('Failed to add tenant');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this tenant?')) return;

    try {
      await axios.delete(`/api/admin/tenants/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTenants();
    } catch (error) {
      setError('Failed to delete tenant');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate input before sending
    const validation = validateData(PasswordChangeSchema, passwordChange);
    if (!validation.isValid) {
      setError(validation.error);
      setIsLoading(false);
      return;
    }

    try {
      await axios.post('/api/auth/admin/change-password', validation.data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPasswordChange({ currentPassword: '', newPassword: '' });
      setShowPasswordChange(false);
      alert('Password changed successfully!');
    } catch (error) {
      setError('Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate input before sending
    const validation = validateData(AdminUserSchema, newAdmin);
    if (!validation.isValid) {
      setError(validation.error);
      setIsLoading(false);
      return;
    }

    try {
      await axios.post('/api/auth/admin/create-user', validation.data, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewAdmin({ username: '', email: '', password: '', role: 'admin' });
      setShowAddAdmin(false);
      alert('Admin user created successfully!');
    } catch (error) {
      setError('Failed to create admin user');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="h-screen bg-gradient-to-br from-blue-950 via-slate-950 to-slate-900 flex items-center justify-center px-4 py-8 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-32 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
          <div className="absolute -bottom-8 left-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-pulse" style={{animationDelay: '4s'}}></div>
        </div>

        <div className="relative z-10 bg-slate-900 bg-opacity-40 backdrop-blur-md max-w-md w-full shadow-2xl rounded-lg p-8 border border-blue-500 border-opacity-10">
          <h2 className="text-2xl font-semibold mb-2 text-white text-center">Admin Portal</h2>
          <p className="text-slate-400 mb-6 text-center">Manage tenant configurations and Microsoft Graph integrations</p>

          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-blue-400 mb-2">Username</label>
              <input
                type="text"
                value={loginData.username}
                onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                required
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-blue-400 mb-2">Password</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                required
              />
            </div>
            {error && <div className="text-red-400 text-sm mb-4 bg-red-500 bg-opacity-10 p-3 rounded border border-red-500 border-opacity-20">{error}</div>}
            <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>

        </div>
      </div>
    );
  }

  const fetchTenantUsers = async (tenantId, page = 1) => {
    try {
      const response = await axios.get(`/api/admin/tenants/${tenantId}/users?page=${page}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch tenant users');
    }
  };

  const viewTenantUsers = async (tenant) => {
    setSelectedTenant(tenant);
    setCurrentView('tenant-users');
    await fetchTenantUsers(tenant.id);
  };

  const goBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedTenant(null);
  };

  const viewAuditLogs = () => {
    setCurrentView('audit-logs');
  };

  const handleLogout = () => {
    // Clear sensitive data
    setToken('');
    clientSecretRef.current = '';
    setAdminData(null);
    setIsAuthenticated(false);
    setNewTenant({
      name: '',
      domain: '',
      clientId: '',
      tenantId: '',
      authority: '',
      allowPasswordReset: true,
      allowAccountUnlock: true
    });
    setNewAdmin({ username: '', email: '', password: '', role: 'admin' });
    setPasswordChange({ currentPassword: '', newPassword: '' });

    // Redirect to home
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-slate-950 to-slate-900">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <div className="w-64 bg-slate-900 bg-opacity-40 backdrop-blur-md border-r border-blue-500 border-opacity-10 p-6">
          <div className="sidebar-menu space-y-1">
            <h3 className="text-blue-300 text-xs font-semibold uppercase tracking-widest px-4 py-3 text-opacity-70">Navigation</h3>
            <button
              onClick={() => setCurrentView('dashboard')}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                currentView === 'dashboard'
                  ? 'bg-blue-600 bg-opacity-80 text-white shadow-lg'
                  : 'text-slate-300 hover:bg-blue-500 hover:bg-opacity-15'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={viewAuditLogs}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                currentView === 'audit-logs'
                  ? 'bg-blue-600 bg-opacity-80 text-white shadow-lg'
                  : 'text-slate-300 hover:bg-blue-500 hover:bg-opacity-15'
              }`}
            >
              Audit Logs
            </button>

            <div className="pt-2 mt-4 border-t border-blue-500 border-opacity-20">
              <h3 className="text-blue-300 text-xs font-semibold uppercase tracking-widest px-4 py-3 text-opacity-70">Account</h3>
              <button
                onClick={() => setShowPasswordChange(!showPasswordChange)}
                className="w-full text-left px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-blue-500 hover:bg-opacity-15 transition-all duration-200"
              >
                Change Password
              </button>
            </div>

            {adminData?.role === 'super_admin' && (
              <div className="pt-2 mt-4 border-t border-blue-500 border-opacity-20">
                <h3 className="text-blue-300 text-xs font-semibold uppercase tracking-widest px-4 py-3 text-opacity-70">Management</h3>
                <button
                  onClick={() => setShowAddTenant(!showAddTenant)}
                  className="w-full text-left px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-blue-500 hover:bg-opacity-15 transition-all duration-200"
                >
                  Add Tenant
                </button>
                <button
                  onClick={() => setShowAddAdmin(!showAddAdmin)}
                  className="w-full text-left px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-blue-500 hover:bg-opacity-15 transition-all duration-200 mt-2"
                >
                  Add Admin
                </button>
              </div>
            )}

            <div className="pt-2 mt-4 border-t border-blue-500 border-opacity-20">
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-3 rounded-lg font-medium text-red-400 hover:bg-red-500 hover:bg-opacity-15 transition-all duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {currentView === 'dashboard' && (
            <>
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-slate-400 mt-2">Welcome, {adminData?.username}</p>
              </div>

              {showAddTenant && (
                <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-6 border border-blue-500 border-opacity-10 mb-8">
                  <h3 className="text-xl font-semibold mb-6 text-white">Add New Tenant</h3>
                  <form onSubmit={handleAddTenant} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-blue-400 mb-2">Organization Name</label>
                        <input
                          type="text"
                          value={newTenant.name}
                          onChange={(e) => setNewTenant({...newTenant, name: e.target.value})}
                          placeholder="Acme Corp"
                          className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-blue-400 mb-2">Domain</label>
                        <input
                          type="text"
                          value={newTenant.domain}
                          onChange={(e) => setNewTenant({...newTenant, domain: e.target.value})}
                          placeholder="example.com"
                          className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-blue-400 mb-2">Client ID</label>
                        <input
                          type="text"
                          value={newTenant.clientId}
                          onChange={(e) => setNewTenant({...newTenant, clientId: e.target.value})}
                          placeholder="12345678-1234-1234-1234-123456789012"
                          className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-blue-400 mb-2">Client Secret</label>
                        <input
                          type="password"
                          ref={clientSecretRef}
                          onChange={(e) => { clientSecretRef.current = e.target.value; }}
                          placeholder="••••••••••••••••"
                          className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-blue-400 mb-2">Tenant ID</label>
                        <input
                          type="text"
                          value={newTenant.tenantId}
                          onChange={(e) => {
                            const tenantId = e.target.value;
                            setNewTenant({
                              ...newTenant,
                              tenantId,
                              authority: `https://login.microsoftonline.com/${tenantId}`
                            });
                          }}
                          placeholder="87654321-4321-8765-dcba-210987654321"
                          className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <button type="submit" className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading}>
                        {isLoading ? 'Adding...' : 'Add Tenant'}
                      </button>
                      <button type="button" onClick={() => setShowAddTenant(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {showAddAdmin && adminData?.role === 'super_admin' && (
                <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-6 border border-blue-500 border-opacity-10 mb-8">
                  <h3 className="text-xl font-semibold mb-6 text-white">Add New Admin User</h3>
                  <form onSubmit={handleAddAdmin} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-blue-400 mb-2">Username</label>
                        <input
                          type="text"
                          value={newAdmin.username}
                          onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                          placeholder="Enter username"
                          className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-blue-400 mb-2">Email</label>
                        <input
                          type="email"
                          value={newAdmin.email}
                          onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                          placeholder="admin@example.com"
                          className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-blue-400 mb-2">Password (minimum 8 characters)</label>
                        <input
                          type="password"
                          value={newAdmin.password}
                          onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                          placeholder="Enter secure password"
                          minLength="8"
                          className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-blue-400 mb-2">Role</label>
                        <select
                          value={newAdmin.role}
                          onChange={(e) => setNewAdmin({...newAdmin, role: e.target.value})}
                          className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                          required
                        >
                          <option value="admin">Admin</option>
                          {adminData?.role === 'super_admin' && (
                            <option value="super_admin">Super Admin</option>
                          )}
                        </select>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <button type="submit" className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading}>
                        {isLoading ? 'Creating...' : 'Create Admin User'}
                      </button>
                      <button type="button" onClick={() => setShowAddAdmin(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </>
          )}

          {currentView === 'tenant-users' && (
            <>
              <div className="mb-8 flex items-center gap-4">
                <button
                  onClick={goBackToDashboard}
                  className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                >
                  ← Back to Dashboard
                </button>
                <h1 className="text-3xl font-bold text-white">Users for {selectedTenant?.name}</h1>
              </div>
            </>
          )}

          {currentView === 'audit-logs' && (
            <AuditLogs token={token} onBack={goBackToDashboard} />
          )}

          {error && <div className="text-red-400 text-sm mb-4 bg-red-500 bg-opacity-10 p-3 rounded border border-red-500 border-opacity-20">{error}</div>}

          {showPasswordChange && (
            <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-6 border border-blue-500 border-opacity-10 mb-8">
              <h3 className="text-xl font-semibold mb-6 text-white">Change Password</h3>
              <form onSubmit={handleChangePassword} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-blue-400 mb-2">Current Password</label>
                  <input
                    type="password"
                    value={passwordChange.currentPassword}
                    onChange={(e) => setPasswordChange({...passwordChange, currentPassword: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-400 mb-2">New Password (minimum 8 characters)</label>
                  <input
                    type="password"
                    value={passwordChange.newPassword}
                    onChange={(e) => setPasswordChange({...passwordChange, newPassword: e.target.value})}
                    minLength="8"
                    className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
                    required
                  />
                </div>
                <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading}>
                  {isLoading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>
          )}

          {showUsers && adminData?.role === 'super_admin' && (
        <div className="users-section">
          <h3>System Users</h3>
          
          <div className="users-category">
            <h4>Admin Users ({users.adminUsers.length})</h4>
            {users.adminUsers.length === 0 ? (
              <div className="empty-state">
                <p>No admin users found.</p>
              </div>
            ) : (
              <div className="users-grid">
                {users.adminUsers.map(admin => (
                  <div key={admin.id} className="user-card admin-user">
                    <div className="user-header">
                      <h5>{admin.username}</h5>
                      <div className="user-status">
                        <span className={`role-badge ${admin.role}`}>{admin.role}</span>
                        {admin.isActive ? (
                          <span className="status-active">Active</span>
                        ) : (
                          <span className="status-inactive">Inactive</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="user-details">
                      <p><strong>Email:</strong> {admin.email}</p>
                      <p><strong>Created:</strong> {new Date(admin.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}

      {currentView === 'dashboard' && (
        <div>
          <h3 className="text-xl font-semibold mb-6 text-white">Configured Tenants</h3>
          {tenants.length === 0 ? (
            <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-12 border border-blue-500 border-opacity-10 text-center">
              <p className="text-slate-400">No tenants configured yet. Add your first tenant above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tenants.map(tenant => (
                <div key={tenant.id} className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-6 border border-blue-500 border-opacity-10">
                  <div className="flex items-start justify-between mb-4">
                    <h4 className="text-lg font-semibold text-white">{tenant.name}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      tenant.isActive
                        ? 'bg-green-500 bg-opacity-20 text-green-400'
                        : 'bg-red-500 bg-opacity-20 text-red-400'
                    }`}>
                      {tenant.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4 text-sm text-slate-300">
                    <p><strong className="text-blue-400">Domain:</strong> {tenant.domain}</p>
                    <p><strong className="text-blue-400">Client ID:</strong> <code className="text-xs bg-slate-800 bg-opacity-50 px-2 py-1 rounded">{tenant.clientId.substring(0, 12)}...</code></p>
                    <p><strong className="text-blue-400">Tenant ID:</strong> <code className="text-xs bg-slate-800 bg-opacity-50 px-2 py-1 rounded">{tenant.tenantId.substring(0, 12)}...</code></p>
                  </div>

                  <div className="space-y-2 mb-6 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Password Reset:</span>
                      <span className={tenant.allowPasswordReset ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>
                        {tenant.allowPasswordReset ? '✓ Enabled' : '✗ Disabled'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Account Unlock:</span>
                      <span className={tenant.allowAccountUnlock ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>
                        {tenant.allowAccountUnlock ? '✓ Enabled' : '✗ Disabled'}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:shadow-lg"
                      onClick={() => viewTenantUsers(tenant)}
                    >
                      View Users
                    </button>
                    {adminData?.role === 'super_admin' && (
                      <button
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                        onClick={() => handleDeleteTenant(tenant.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

          {currentView === 'tenant-users' && selectedTenant && (
            <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg border border-blue-500 border-opacity-10 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-blue-500 border-opacity-10">
                    <th className="text-left px-4 py-3 text-blue-400 font-semibold">Name</th>
                    <th className="text-left px-4 py-3 text-blue-400 font-semibold">Email</th>
                    <th className="text-left px-4 py-3 text-blue-400 font-semibold">Phone</th>
                    <th className="text-left px-4 py-3 text-blue-400 font-semibold">Status</th>
                    <th className="text-left px-4 py-3 text-blue-400 font-semibold">Joined</th>
                    <th className="text-left px-4 py-3 text-blue-400 font-semibold">Last Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantUsers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-slate-400">No users found for this tenant.</td>
                    </tr>
                  ) : (
                    tenantUsers.map(user => (
                      <tr key={user.id} className="border-b border-blue-500 border-opacity-10 hover:bg-blue-500 hover:bg-opacity-5 transition-colors">
                        <td className="px-4 py-3 text-sm text-slate-300">{user.displayName || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-300">{user.email}</td>
                        <td className="px-4 py-3 text-sm text-slate-300">{user.phoneNumber || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            user.isVerified
                              ? 'bg-green-500 bg-opacity-20 text-green-400'
                              : 'bg-yellow-500 bg-opacity-20 text-yellow-400'
                          }`}>
                            {user.isVerified ? '✓ Verified' : '⏳ Unverified'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300">{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-sm text-slate-300">{user.lastVerified ? new Date(user.lastVerified).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 p-4 border-t border-blue-500 border-opacity-10">
                  <button
                    className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!pagination.hasPrevPage}
                    onClick={() => fetchTenantUsers(selectedTenant.id, pagination.currentPage - 1)}
                  >
                    ← Previous
                  </button>
                  <span className="text-slate-300 font-semibold">
                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalUsers} total users)
                  </span>
                  <button
                    className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!pagination.hasNextPage}
                    onClick={() => fetchTenantUsers(selectedTenant.id, pagination.currentPage + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;